"use client";

import Link from "next/link";
import { Home, BookOpen, LayoutDashboard, Settings, Shield, Clock, Users as UsersIcon, Sparkles } from "lucide-react";
import { usePathname } from "next/navigation";
import { cn } from "@/lib/utils";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { useRef } from "react";

interface PWABottomNavProps {
  isAuthenticated: boolean;
  isAdmin: boolean;
  displayName?: string;
}

/**
 * PWA Bottom Navigation Bar
 * Fixed bottom navigation for mobile app experience
 * - Logged-in users: Dashboard, Courses, Right Now, Next Gen, Account (+ Admin if admin)
 * - Logged-out users: Home, Courses, Right Now, Next Gen
 */
export function PWABottomNav({ isAuthenticated, isAdmin }: PWABottomNavProps) {
  const pathname = usePathname();
  const formRef = useRef<HTMLFormElement | null>(null);
  const switchAccountFormRef = useRef<HTMLFormElement | null>(null);

  // Common navigation items (always visible)
  const commonNavItems = [
    { href: "/courses", icon: BookOpen, label: "Courses" },
    { href: "/right-now", icon: Clock, label: "Right Now" },
    { href: "/next-gen", icon: Sparkles, label: "Next Gen" },
  ];

  // Build navigation based on authentication
  let navItems;
  if (isAuthenticated) {
    // Logged-in users see: Dashboard + common items
    navItems = [
      { href: "/dashboard", icon: LayoutDashboard, label: "Dashboard" },
      ...commonNavItems,
    ];

    // Add admin item if user is admin
    if (isAdmin) {
      navItems.push({ href: "/admin", icon: Shield, label: "Admin" });
    }
  } else {
    // Logged-out users see: Home + common items
    navItems = [
      { href: "/", icon: Home, label: "Home" },
      ...commonNavItems,
    ];
  }

  return (
    <>
      <nav className="fixed bottom-0 left-0 right-0 z-50 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 pb-safe">
        <div className="container flex h-16 items-center justify-around" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive =
              pathname === item.href ||
              (item.href !== "/" && pathname.startsWith(item.href));

            return (
              <Link
                key={item.href}
                href={item.href}
                className={cn(
                  "flex flex-col items-center justify-center gap-1 px-3 py-2 transition-colors min-w-[60px]",
                  isActive
                    ? "text-primary"
                    : "text-muted-foreground hover:text-foreground"
                )}
              >
                <Icon className="h-5 w-5" />
                <span className="text-xs font-medium">{item.label}</span>
              </Link>
            );
          })}

          {/* Account dropdown button (only when authenticated) */}
          {isAuthenticated && (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  className={cn(
                    "flex flex-col items-center justify-center gap-1 px-3 py-2 transition-colors min-w-[60px]",
                    pathname.startsWith("/account")
                      ? "text-primary"
                      : "text-muted-foreground hover:text-foreground"
                  )}
                >
                  <Settings className="h-5 w-5" />
                  <span className="text-xs font-medium">Account</span>
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content
                  sideOffset={12}
                  align="end"
                  className="z-[100] min-w-[200px] rounded-md border border-border/70 bg-background p-1 text-sm shadow-lg"
                >
                  <DropdownMenu.Item asChild>
                    <Link
                      href="/dashboard"
                      className="block cursor-pointer rounded px-3 py-2 text-foreground hover:bg-muted"
                    >
                      Dashboard
                    </Link>
                  </DropdownMenu.Item>
                  <DropdownMenu.Item asChild>
                    <Link
                      href="/account"
                      className="block cursor-pointer rounded px-3 py-2 text-foreground hover:bg-muted"
                    >
                      Account Settings
                    </Link>
                  </DropdownMenu.Item>
                  {isAdmin && (
                    <DropdownMenu.Item asChild>
                      <Link
                        href="/admin"
                        className="block cursor-pointer rounded px-3 py-2 text-foreground hover:bg-muted"
                      >
                        Admin Dashboard
                      </Link>
                    </DropdownMenu.Item>
                  )}
                  <DropdownMenu.Separator className="my-1 h-px bg-border/70" />
                  <DropdownMenu.Item
                    className="flex cursor-pointer items-center gap-2 rounded px-3 py-2 text-foreground hover:bg-muted"
                    onSelect={(e) => {
                      e.preventDefault();
                      switchAccountFormRef.current?.requestSubmit();
                    }}
                  >
                    <UsersIcon className="h-4 w-4" />
                    Switch Account
                  </DropdownMenu.Item>
                  <DropdownMenu.Item
                    className="block cursor-pointer rounded px-3 py-2 text-foreground hover:bg-muted"
                    onSelect={(e) => {
                      e.preventDefault();
                      formRef.current?.requestSubmit();
                    }}
                  >
                    Sign out
                  </DropdownMenu.Item>
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          )}
        </div>
      </nav>

      {/* Hidden sign-out form */}
      {isAuthenticated && (
        <>
          <form ref={formRef} action="/auth/sign-out" method="post" hidden />
          <form
            ref={switchAccountFormRef}
            action="/api/auth/planning-center/switch"
            method="post"
            hidden
          />
        </>
      )}
    </>
  );
}

